import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";

import { AuthMenu } from "@/components/AuthMenu";
import { useCurrentUser } from "@/hooks/useAuth";
import {
  type AdminUserRow,
  useAdminUsers,
  useDeleteUser,
  useToggleUserAdmin,
} from "@/hooks/useAdminUsers";

function formatDate(s: string | null): string {
  if (!s) return "—";
  const d = new Date(s);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function AdminUsersPage() {
  const navigate = useNavigate();
  const { data: me, isLoading: authLoading } = useCurrentUser();
  const { data, isLoading, error, refetch } = useAdminUsers();
  const { mutate: toggleAdmin, isPending: isToggling, variables: toggleVars } =
    useToggleUserAdmin();
  const { mutate: deleteUser, isPending: isDeleting, variables: deleteVars } =
    useDeleteUser();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!me || !me.is_admin) {
      navigate("/", { replace: true });
    }
  }, [authLoading, me, navigate]);

  if (authLoading || !me || !me.is_admin) return null;

  function onToggle(u: AdminUserRow) {
    setErrorMsg(null);
    toggleAdmin(
      { userId: u.id, isAdmin: !u.is_admin },
      { onError: (e) => setErrorMsg((e as Error).message) },
    );
  }

  function onDelete(u: AdminUserRow) {
    setErrorMsg(null);
    const ok = window.confirm(
      `Delete ${u.username}?\n\nThis hard-deletes the account and cascades through their favorites (${u.favorites_count}), saved searches (${u.saved_searches_count}), and blacklist (${u.blacklist_count}). Download jobs they created stay (anonymous).`,
    );
    if (!ok) return;
    deleteUser(u.id, {
      onError: (e) => setErrorMsg((e as Error).message),
    });
  }

  return (
    <>
      <Helmet>
        <title>Users — Admin</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-surface-0 flex flex-col">
        <div className="sticky top-0 z-30 bg-surface-0/95 backdrop-blur border-b border-surface-2 px-4 py-3">
          <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-x-3 gap-y-2">
            <Link
              to="/"
              className="text-sm text-zinc-400 hover:text-accent-light min-h-[40px] inline-flex items-center"
            >
              ← Search
            </Link>
            <h1 className="text-sm font-semibold">Users</h1>
            <span className="text-[10px] uppercase tracking-wider text-accent-light">
              admin
            </span>
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => refetch()}
                className="text-xs text-zinc-400 hover:text-accent-light min-h-[40px] px-2 inline-flex items-center"
                title="Refresh"
              >
                ↻ refresh
              </button>
              <AuthMenu />
            </div>
          </div>
        </div>

        <div className="flex-1 px-4 py-6 max-w-5xl mx-auto w-full space-y-4">
          {error && (
            <div className="bg-red-900/30 border border-red-800 text-red-200 text-sm rounded p-3">
              Failed to load users: {(error as Error).message}
            </div>
          )}
          {errorMsg && (
            <div className="bg-red-900/30 border border-red-800 text-red-200 text-sm rounded p-3">
              {errorMsg}
            </div>
          )}

          {isLoading || !data ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-surface-2 h-12 rounded animate-pulse" />
              ))}
            </div>
          ) : data.users.length === 0 ? (
            <div className="bg-surface-1 border border-surface-3 rounded p-6 text-center text-sm text-zinc-500">
              No users yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-zinc-500 text-left border-b border-surface-3">
                    <th className="py-2 pr-3">Username</th>
                    <th className="py-2 pr-3">Joined</th>
                    <th className="py-2 pr-3">Last login</th>
                    <th className="py-2 pr-3 text-right">♥</th>
                    <th className="py-2 pr-3 text-right">★</th>
                    <th className="py-2 pr-3 text-right">⛔</th>
                    <th className="py-2 pr-3 text-center">Admin</th>
                    <th className="py-2 pr-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.map((u) => {
                    const isSelf = u.id === me.id;
                    const togglingThis =
                      isToggling && toggleVars?.userId === u.id;
                    const deletingThis =
                      isDeleting && deleteVars === u.id;
                    return (
                      <tr
                        key={u.id}
                        className={`border-b border-surface-2/50 hover:bg-surface-1 ${
                          isSelf ? "bg-surface-1/30" : ""
                        }`}
                      >
                        <td className="py-2 pr-3 font-mono text-accent-light">
                          {u.username}
                          {isSelf && (
                            <span className="ml-1 text-[10px] uppercase text-zinc-500">
                              (you)
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-zinc-400 text-xs">
                          {formatDate(u.created_at)}
                        </td>
                        <td className="py-2 pr-3 text-zinc-400 text-xs">
                          {formatDate(u.last_login_at)}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums text-zinc-400">
                          {u.favorites_count}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums text-zinc-400">
                          {u.saved_searches_count}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums text-zinc-400">
                          {u.blacklist_count}
                        </td>
                        <td className="py-2 pr-3 text-center">
                          <button
                            onClick={() => onToggle(u)}
                            disabled={isSelf || togglingThis}
                            title={
                              isSelf
                                ? "You can't promote or demote yourself."
                                : u.is_admin
                                  ? "Demote to regular user"
                                  : "Promote to admin"
                            }
                            className={`text-xs px-2 py-1 min-h-[32px] rounded transition-colors ${
                              isSelf
                                ? "bg-surface-2 text-zinc-600 cursor-not-allowed"
                                : u.is_admin
                                  ? "bg-accent/20 text-accent-light hover:bg-accent/30"
                                  : "bg-surface-2 text-zinc-400 hover:bg-surface-3"
                            }`}
                          >
                            {togglingThis ? "…" : u.is_admin ? "admin" : "user"}
                          </button>
                        </td>
                        <td className="py-2 pr-3 text-right">
                          <button
                            onClick={() => onDelete(u)}
                            disabled={isSelf || deletingThis}
                            title={
                              isSelf
                                ? "You can't delete yourself."
                                : "Hard-delete this user and cascade their per-user data."
                            }
                            className={`text-xs px-2 py-1 min-h-[32px] rounded transition-colors ${
                              isSelf
                                ? "bg-surface-2 text-zinc-600 cursor-not-allowed"
                                : "bg-red-900/30 text-red-300 hover:bg-red-900/60"
                            }`}
                          >
                            {deletingThis ? "deleting…" : "delete"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-[10px] text-zinc-600">
            {data?.users.length ?? 0} user
            {data && data.users.length === 1 ? "" : "s"}. Deletes cascade
            through favorites, saved searches, and blacklist. Download jobs
            persist with their <code className="font-mono">user_id</code> set
            to NULL.
          </p>
        </div>
      </div>
    </>
  );
}
